import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AttestorLogRepositoryService, BundlesRepositoryService, ConfigurationState, Order, OrderManagerService, OrderMessagesComponent, OrderRepositoryService, OrderStateRepositoryService, SelectOption, UboTreeNode, UboTreeNodesService, Validation, ValidationService, eOrderType, eOrderTypeGroup } from 'reg-hub-common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmSubmitComponent } from '../confirm-submit/confirm-submit.component';
import { firstValueFrom, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { ConfirmDeleteComponent } from '../confirm-delete/confirm-delete.component';
import { ConfirmCancelComponent } from '../confirm-cancel/confirm-cancel.component';
import { NavigationHistoryService } from '../../services/navigation-history-service/navigation-history.service';
import { OrdersHubService } from '../../services/hubs/orders-hub/orders-hub.service';
import { Constants } from 'projects/reg-hub-client/src/constants';
import { AddToBundleComponent } from '../add-to-bundle/add-to-bundle.component';

@Component({
  selector: 'app-new-business-search',
  templateUrl: './new-business-search.component.html',
  styleUrls: ['./new-business-search.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NewBusinessSearchComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private orderStateSubscription?: Subscription;

  orderId: string = "";
  order: Order = {
    referenceNumber: "",
    country: "CA",
    jurisdiction: "",
    orderTypeID: eOrderType.notSet,
  } as Order;

  orderTypeGroupID: eOrderTypeGroup = eOrderTypeGroup.BusinessSearch;
  isLoadingOrder: boolean = false;
  finalStepIsSelected: boolean = false;
  showOrderFees: boolean = false;
  private bundleOptions: SelectOption[] = [];

  constructor(private activatedRoute: ActivatedRoute,
    private orderRepo: OrderRepositoryService,
    private orderManager: OrderManagerService,
    private orderStateRepo: OrderStateRepositoryService,
    private dialog: MatDialog,
    private router: Router,
    private navigationService: NavigationHistoryService,
    private ordersHub: OrdersHubService,
    private route: ActivatedRoute,
    private configurationState: ConfigurationState,
    private bundlesRepo: BundlesRepositoryService,
    private uboTreeService: UboTreeNodesService,
    private attestorLogService: AttestorLogRepositoryService) { }

  ngOnInit(): void {
    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => this.order = order);

    this.shouldShowOrderSummary();

    this.activatedRoute.paramMap.subscribe(params => {
      this.orderId = params.get('orderID')!;
      if (this.orderId) {
        // If we have an orderID, load the associated order and stepper
        this.getOrder(this.orderId);
      } else {
        this.resetOrder();
        // if we don't have an orderID, load base stepper
        this.orderManager.updateOrder(this.order);
      }
    });
  }

  private getOrder = (orderID: string) => {
    this.isLoadingOrder = true;
    this.orderRepo.getOrder('orders', orderID)
      .subscribe(order => {
        this.isLoadingOrder = false;
        this.order = order as Order;

        this.orderManager.updateOrder(this.order);
        this.getBundles();
      });
  };

  messageCount() {
    let length = this.order?.orderMessages?.filter((u) => u.isCustomerVisible && !u.isReadByCustomer).length ?? 0;
    return length > 0 ? length.toString() : "";
  }

  shouldShowOrderMessages(): boolean {
    return this.order.orderStatusTypeID == 'Invalid'
      || this.messageCount() != "";
  }

  shouldShowOrderSummary() {
    this.configurationState.hasOrderFeeVisibility().then(show => {
      this.showOrderFees = show;
    });
  }

  openOrderMessages() {
    this.dialog.open(OrderMessagesComponent, {
      data: { order: this.order, customer: true },
      minWidth: '800px'
    });
  }

  submissionConfirmation(order: Order) {
    const originatingOrderIDParam = this.route.snapshot.queryParamMap.get('originatingOrderID');
    const destination = this.route.snapshot.queryParamMap.get('destination');

    const ref = this.dialog.open(ConfirmSubmitComponent, {
      data:
      {
        order: this.order,
        returnOption: originatingOrderIDParam != null && destination === Constants.returnToOriginatingOrder,
      }
    });

    ref.afterClosed().subscribe(async res => {
      if (!res.submit) {
        return;
      }

      if (res.uboTreesToLinkTo) {
        for (let i = 0; i < res.uboTreesToLinkTo.length; i++) {
          this.uboTreeService.addOrderToNode(res.uboTreesToLinkTo[i].id, this.order.id).subscribe();
        }
      }

      if (res.shouldSendEmail && res.targetEmails?.length > 0) {
        this.addEmailSubscriptionsThenSubmit(res.targetEmails, res.submit);
      } else {
        this.submitOrder(res.submit);
      }
    });
  }

  submitOrder(destination: string) {
    //Limit flashing of order spinner
    var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

    const pendingStatus = {
      newStatus: "Pending",
      status: "",
      message: "Submitted from Reg Hub Client"
    };

    this.orderStateSubscription = this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
      .subscribe({
        next: (result) => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;

          if (result) {
            this.ordersHub.watchOrder(this.order);
            this.order.orderStatusTypeID = "Pending";
            this.orderManager.updateOrder(this.order);

            const originatingOrderIDParam = this.route.snapshot.queryParamMap.get('originatingOrderID');
            if (destination == Constants.returnToOriginatingOrder
              && originatingOrderIDParam != null) {
              this.router.navigate(['orders', originatingOrderIDParam], {
                queryParams: {}
              });
            } else {
              this.router.navigate(['orders', this.order.id]);
            }
          }
        },
        error: () => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;
        }
      });
  }

  deletionConfirmation() {
    if (this.order?.orderStatusTypeID == "Draft" ||
      this.order?.orderStatusTypeID == "HistoricalDraft") {
      const dialogRef = this.dialog.open(ConfirmDeleteComponent);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // deletion is confirmed, delet order
          this.deleteOrder();
        }
      });
    }
  }

  deleteOrder() {
    const orderID = this.order.id;

    this.orderRepo.deleteOrder("Orders/" + this.order.id)
      .subscribe({
        next: () => {
          let previousUrl = this.navigationService.getOrderDeletionRedirectUrl(orderID);
          this.router.navigateByUrl(previousUrl);
        },
        error: (error) => {
          console.log(error);
        }
      });
  }

  cancelConfirmation() {
    const dialogRef = this.dialog.open(ConfirmCancelComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // deletion is confirmed, delet order
        this.cancelOrder();
      }
    });
  }

  cancelOrder() {
    var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

    const pendingStatus = {
      newStatus: "Cancelled",
      status: "",
      message: "Cancelled from Reg Hub Client"
    };

    this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
      .subscribe({
        next: (result) => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;

          if (!result) {
            this.order.orderStatusTypeID = "Cancelled";
            this.orderManager.updateOrder(this.order);
            this.router.navigate(['orders', this.order.id]);
          }
        },
        error: () => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;
        }
      });
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.uboTreeService.clearSubjects();

    // Unsubscribe to prevent memory leaks
    if (this.orderStateSubscription) {
      this.orderStateSubscription.unsubscribe();
    }
  }

  private resetOrder() {
    this.order = {
      referenceNumber: "",
      country: "CA",
      jurisdiction: "",
      orderTypeID: eOrderType.notSet,
    } as Order;
  }

  getBundles() {
    if (this.order.orderGroup) {
      this.bundlesRepo.getBundlesAsSelectOptions(this.order.orderGroup!.corporationID).subscribe(bundles => {
        this.bundleOptions = bundles;
      });
    }
  }

  getAvailableBundles(): SelectOption[] {
    var orderBundles = this.order.orderGroup?.bundles?.map(bundle => bundle.id) ?? [];

    return this.bundleOptions.filter(opt => !orderBundles.includes(opt.value as string));
  }

  showBundlesButton() {
    let availableBundles = this.getAvailableBundles();

    return availableBundles.length > 0;
  }

  addToBundle() {
    const dialogRef = this.dialog.open(AddToBundleComponent, {
      data: { order: this.order, bundles: this.getAvailableBundles() },
      minWidth: '500px'
    });
  }

  // subscirptions/- tells the API to append to the end of the subscriptions array rather than replace it
  protected addEmailSubscriptionsThenSubmit(emails: string[], destination?: any) {
    const subscriptionPatches = emails.map(email => ({
      op: 'add',
      path: 'subscriptions/-',
      value: {
        'triggerOrderStatusTypeID': 'Complete',
        'subscriptionType': 'Email',
        'destination': email
      }
    }));

    this.orderRepo.patchOrderReturnOrder(this.order.id, subscriptionPatches)
      .subscribe(() => {
        for (let email of emails) {
          firstValueFrom(this.attestorLogService.createAttestorLog({
            customerVisible: true,
            message: `Order Complete email subscription added for: ${email}`,
            orderId: this.order.id
          }));
          this.submitOrder(destination);
        }
      });
  }
}
